import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Collapse, Container, IconButton, List, ListItem, ListItemButton, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ItemTypes,
  goToControl,
  selectAllGroups,
  selectControlItemInFocus,
  selectFilteredCatalogs,
  selectFilteredSubControls,
  selectScrollReferenceId,
  selectSelectedGroupId,
} from '../../redux/reducers/assessmentReducer';
import { selectTitle } from '../../redux/reducers/catalogsReducer';

function getDepth(id) {
  let depth = 0;
  const catalogParts = id.split(':');
  if (catalogParts.length > 2) {
    const groupParts = catalogParts.at(-1).split('.');
    depth = groupParts.length;
  }
  return depth;
}

function ControlRow({ control }) {
  const dispatch = useDispatch();
  const title = useSelector((state) => selectTitle(state, control.id));
  const controlItemInFocus = useSelector(selectControlItemInFocus);

  const handleSelect = () => {
    dispatch(goToControl(control.id));
  };
  return (
    <ListItem>
      <ListItemButton
        id={`${title}-select-control`}
        selected={controlItemInFocus === control.id}
        variant="evaluationListText"
        onClick={() => handleSelect()}
      >
        <Typography>{title}</Typography>
      </ListItemButton>
    </ListItem>
  );
}

function GroupRow({ group, handleGroupExpanded, groupExpanded }) {
  const dispatch = useDispatch();
  const controls = useSelector((state) => selectFilteredSubControls(state, group.id));
  const title = useSelector((state) => selectTitle(state, group.id));
  const selectedId = useSelector(selectSelectedGroupId);
  const depth = getDepth(group.id);

  const handleSelect = () => {
    dispatch(goToControl(group.id));
  };

  const getMargin = (d) => {
    let rval = 0;
    if (d === 1) {
      rval = 1;
    } else if (d > 1) {
      rval = 1 + 0.5 * (d - 1);
    }
    return rval;
  };

  return (
    <div key={`${group.id}-div`}>
      <ListItem
        disableGutters
        variant={selectedId === group.id ? 'selected' : ''}
        sx={{ display: 'flex', justifyContent: 'space-between' }}
      >
        <ListItemButton id={`select-${title}`} variant="evaluationListText" onClick={() => handleSelect()}>
          <Typography variant={`controlList${depth}`}>{title}</Typography>
        </ListItemButton>
        <IconButton id={`${title}-expand-list`} onClick={() => handleGroupExpanded(group.id)}>
          {groupExpanded[group.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </ListItem>
      <Collapse in={groupExpanded[group.id]} timeout="auto">
        <List dense sx={{ px: getMargin(depth) }}>
          {controls.map((control) => (
            control.type === ItemTypes.GROUP ? (
              <GroupRow
                key={`${control.id}-row`}
                group={control}
                handleGroupExpanded={handleGroupExpanded}
                groupExpanded={groupExpanded}
              />
            ) : (
              <ControlRow key={`${control.id}-row`} control={control} />
            )
          ))}
        </List>
      </Collapse>
    </div>
  );
}

function EvaluationList() {
  const filteredCatalogs = useSelector(selectFilteredCatalogs);
  const allGroups = useSelector(selectAllGroups);
  const scrollReferenceId = useSelector(selectScrollReferenceId);

  const initGroupExpanded = useCallback(() => {
    const initState = {};
    allGroups.forEach((group) => {
      initState[group.id] = group.id.split(':').length === 2;
    });
    return initState;
  }, [allGroups]);

  const initialState = useMemo(() => initGroupExpanded(), [allGroups]);

  const [groupExpanded, setGroupExpanded] = useState(initialState);

  const handleGroupExpanded = (groupId, expand = false) => {
    const newState = { ...groupExpanded };
    if (groupExpanded[groupId] && !expand) {
      const groupsToChange = Object.keys(groupExpanded).filter((key) => key.startsWith(groupId));
      groupsToChange.forEach((group) => {
        newState[group] = false;
      });
    } else {
      const catalogId = groupId.split(':').slice(0, -1).join(':');
      newState[catalogId] = true;
      newState[groupId] = true;
      let groupParts = groupId.split('.');
      // expand all parent groups, e.g. both groups 6 & 6.1 need to be expanded when 6.1.1 is selected
      while (groupParts.length > 1) {
        const parentGroup = groupParts.slice(0, -1).join('.');
        newState[parentGroup] = true;
        groupParts = parentGroup.split('.');
      }
    }
    setGroupExpanded(newState);
  };

  useEffect(() => {
    if (scrollReferenceId !== null) {
      const groupId = scrollReferenceId.split('.').slice(0, -1).join('.');
      handleGroupExpanded(groupId, true);
    }
  }, [scrollReferenceId]);

  return (
    <Container disableGutters>
      <List dense variant="controlList">
        {filteredCatalogs.map((catalog) => (
          <GroupRow
            key={`${catalog.id}-row`}
            group={catalog}
            handleGroupExpanded={handleGroupExpanded}
            groupExpanded={groupExpanded}
          />
        ))}
      </List>
    </Container>
  );
}
export default EvaluationList;
